import { useMemo, useRef } from "react"

const COL = { key: "#6ea8fe", str: "#7ec699", num: "#e0a060", kw: "#c191e8", punct: "var(--muted)" }

// Lightweight JSON syntax highlighter → HTML with inline-styled spans.
function highlight(code: string): string {
  const esc = code.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  return esc.replace(
    /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],])/g,
    (_m, str: string, colon: string, kw: string, num: string, punct: string) => {
      if (str !== undefined) {
        const color = colon ? COL.key : COL.str
        return `<span style="color:${color}">${str}</span>${colon ? `<span style="color:${COL.punct}">${colon}</span>` : ""}`
      }
      if (kw) return `<span style="color:${COL.kw}">${kw}</span>`
      if (num) return `<span style="color:${COL.num}">${num}</span>`
      if (punct) return `<span style="color:${COL.punct}">${punct}</span>`
      return _m
    },
  )
}

const shared: React.CSSProperties = {
  margin: 0,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12.5,
  lineHeight: 1.5,
  padding: 12,
  whiteSpace: "pre",
  tabSize: 2,
  border: 0,
  boxSizing: "border-box",
}

export function JsonCodeEditor({ value, onChange, style }: {
  value: string
  onChange: (v: string) => void
  style?: React.CSSProperties
}) {
  const ta = useRef<HTMLTextAreaElement>(null)
  const pre = useRef<HTMLPreElement>(null)
  const html = useMemo(() => highlight(value) + "\n", [value])

  const sync = () => {
    if (pre.current && ta.current) {
      pre.current.scrollTop = ta.current.scrollTop
      pre.current.scrollLeft = ta.current.scrollLeft
    }
  }
  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Tab") {
      e.preventDefault()
      const el = e.currentTarget
      const s = el.selectionStart, end = el.selectionEnd
      const next = value.slice(0, s) + "  " + value.slice(end)
      onChange(next)
      requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + 2 })
    }
  }

  return (
    <div style={{ position: "relative", overflow: "hidden", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", ...style }}>
      <pre ref={pre} aria-hidden style={{ ...shared, position: "absolute", inset: 0, overflow: "auto", color: "var(--text)", pointerEvents: "none" }} dangerouslySetInnerHTML={{ __html: html }} />
      <textarea
        ref={ta}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={sync}
        onKeyDown={onKey}
        spellCheck={false}
        wrap="off"
        style={{ ...shared, position: "relative", width: "100%", height: "100%", resize: "none", background: "transparent", color: "transparent", caretColor: "var(--text)", outline: "none", overflow: "auto" }}
      />
    </div>
  )
}
